import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { RotateCw } from 'lucide-react';
import { apiErrorMessage, marketingSiteService } from '../../services/apiService';
import { useAuthToken } from '../../hooks/useAuthToken';
import { ErrorState } from '../../components/PageState';
import BulkReviewModal from './BulkReviewModal';
import { shortSha } from './ui';

const key = (path, index) => `${path}#${index}`;

/**
 * Site-wide FAQ drift: where the visible FAQ and its FAQPage structured data disagree. Nothing is
 * fixed by default (some structured-data answers are fuller on purpose); the admin picks a
 * direction per item and the choices go out as one reviewed commit.
 */
const FaqReport = ({ canEdit, siteUrl, onOpenPage, onSaved }) => {
  const ensureToken = useAuthToken();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [choices, setChoices] = useState(() => new Map()); // key -> 'TO_JSONLD' | 'TO_VISIBLE'
  const [review, setReview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await ensureToken();
      setReport((await marketingSiteService.faqDrift()).data);
      setChoices(new Map());
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not check the FAQs.'));
    } finally {
      setLoading(false);
    }
  }, [ensureToken]);

  useEffect(() => {
    load();
  }, [load]);

  const params = useMemo(
    () => ({
      choices: [...choices.entries()].map(([k, direction]) => {
        const at = k.lastIndexOf('#');
        return { path: k.slice(0, at), index: Number(k.slice(at + 1)), direction };
      }),
    }),
    [choices]
  );
  const loadPreview = useCallback(async () => (await marketingSiteService.faqDriftPreview(params)).data, [params]);
  const apply = useCallback(
    async ({ baseSha, message }) => (await marketingSiteService.faqDriftApply({ params, baseSha, message })).data,
    [params]
  );

  const choose = (path, index, direction) =>
    setChoices((prev) => {
      const next = new Map(prev);
      if (!direction) next.delete(key(path, index));
      else next.set(key(path, index), direction);
      return next;
    });

  if (loading && !report) {
    return (
      <div className="mse-empty">
        <Spinner animation="border" size="sm" /> Checking every FAQ
      </div>
    );
  }
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!report) return null;

  return (
    <div className="mse-health-report">
      <div className="mse-health-summary">
        <strong>
          {report.driftedItems === 0 ? 'No FAQ differences' : `${report.driftedItems} FAQ item${report.driftedItems === 1 ? '' : 's'} differ`}
        </strong>
        <span className="mse-hint">
          across {report.pagesWithFaq} pages with a FAQ
          {report.pagesWithoutJsonLd > 0 && ` (${report.pagesWithoutJsonLd} without structured data)`}, at{' '}
          <code>{shortSha(report.headSha)}</code>. Differences can be deliberate: choose per item.
        </span>
        <Button
          size="sm"
          variant="outline-secondary"
          className="ms-auto"
          onClick={load}
          disabled={loading}
          title="Check again"
          aria-label="Check again"
        >
          {loading ? <Spinner animation="border" size="sm" /> : <RotateCw size={14} />}
        </Button>
      </div>

      {report.pages.map((page) => (
        <section key={page.path} className="mse-index-card mse-faq-page">
          <div className="mse-field-head">
            <button type="button" className="mse-link-button mse-mono" onClick={() => onOpenPage(page.path)}>
              {page.url}
            </button>
            <span className="mse-hint">
              {page.items} questions{!page.hasJsonLd && ', no structured data'}
              {page.missing?.length > 0 && `, ${page.missing.length} not in the structured data`}
              {page.jsonOnly?.length > 0 && `, ${page.jsonOnly.length} only in the structured data`}
            </span>
          </div>
          {page.drifted.map((item) => {
            const chosen = choices.get(key(page.path, item.index)) || '';
            return (
              <div key={item.index} className="mse-faq-drift">
                <div className="mse-sublabel">
                  {item.index + 1}. {item.question}
                </div>
                <div className="mse-faq-compare">
                  <div>
                    <span className="mse-hint">On the page</span>
                    {item.questionDrift && <div className="mse-faq-q">{item.question}</div>}
                    {item.answerDrift && <div>{item.answer}</div>}
                  </div>
                  <div>
                    <span className="mse-hint">In the structured data</span>
                    {item.questionDrift && <div className="mse-faq-q">{item.jsonQuestion}</div>}
                    {item.answerDrift && <div>{item.jsonAnswer}</div>}
                  </div>
                </div>
                {canEdit && (
                  <Form.Select
                    size="sm"
                    className="mse-faq-choice"
                    value={chosen}
                    onChange={(e) => choose(page.path, item.index, e.target.value)}
                    aria-label={`What to do with ${page.url} question ${item.index + 1}`}
                  >
                    <option value="">Leave as it is</option>
                    <option value="TO_JSONLD">Copy the page text into the structured data</option>
                    <option value="TO_VISIBLE">Copy the structured data onto the page</option>
                  </Form.Select>
                )}
              </div>
            );
          })}
          {page.drifted.length === 0 && <p className="mse-hint mb-0">No differences in the questions both have.</p>}
        </section>
      ))}

      {canEdit && (
        <div className="mse-link-fixbar">
          <span className="mse-hint">{choices.size === 0 ? 'Choose what to do with an item to sync it.' : `${choices.size} item${choices.size === 1 ? '' : 's'} chosen`}</span>
          <Button size="sm" variant="primary" disabled={choices.size === 0} onClick={() => setReview(true)}>
            Sync chosen items
          </Button>
        </div>
      )}
      {review && (
        <BulkReviewModal
          show={review}
          title="Sync FAQ items"
          applyLabel="Save to test site"
          siteUrl={siteUrl}
          loadPreview={loadPreview}
          apply={apply}
          onClose={() => setReview(false)}
          onApplied={() => {
            load();
            onSaved?.();
          }}
        />
      )}
    </div>
  );
};

export default FaqReport;
